import React, { useContext, useMemo, useEffect, useState } from 'react'
import { DesignContext } from 'contexts/DesignContext'
import { useFormikContext } from 'formik'
import CustomCheckboxInput from 'designer/component/element/CheckboxInput'
import objectPath from 'object-path'
import { copyDataByCondition } from 'designer/component/action/Condition'
import {
  getCheckValue,
  getFieldName,
} from 'designer/component/action/Data'
import {
  onCheckCondition,
  onUpdateCondition,
} from 'designer/component/action/condition/Action'
import { debounceCallback } from 'utility/debounceCallback.util'
import { checkSearchField } from 'designer/component/action/Search'
import { getStorage, saveStorage } from 'designer/component/action/Storage'
import { isEmpty } from 'designer/component/action/condition/State'
import PropWrapper from './PropWrapper'
import { updateCheckboxInput } from './DataControl'

const Children = ({ dataProps }) => {
  const {
    props,
    designContext,
    fieldProps,
    fieldName,
    fieldKey,
    errors,
    setIsOnChange,
  } = dataProps
  const {
    jsonForm,
    setFormData,
    isResetData,
    elementAction,
    setIsInputSearch,
    widgetName,
    isUiChanged,
    setIsUiChanged,
  } = designContext

  const { uischema, config } = props
  const input = uischema?.input || {}
  const { label } = uischema
  let {
    uiKey,
    uiName,
    value,
    data,
    isSearch,
    renderWidget,
    mapResponse,
    defaultChecked,
    defaultInput,
    uiInputKey,
    uiInputName,
  } = fieldProps

  const {
    isDynamic = false,
    dynamicName = '',
    dynamicKey = '',
    dynamicIndex = 0,
    dynamicData = {},
    onUpdateFormData = () => {},
    isArray,
    arrayName,
    arrayKey,
  } = config

  const [isUpdate, setIsUpdate] = useState(false)

  const fieldInputName = getFieldName(input.name, { config, widgetName })
  const fieldInputKey = getFieldName(input.key, { config, widgetName })

  let valueChecked = getCheckValue(uischema.valueChecked)
  let valueUnchecked = getCheckValue(uischema.valueUnchecked)

  const formData = jsonForm.formData

  let inputValue = isDynamic
    ? objectPath.get(data, uiInputKey) || objectPath.get(data, uiInputName)
    : objectPath.get(data, uiInputKey)

  const renderCheckboxWidget = objectPath.get(isUiChanged, [widgetName, uiName])
  const renderInputWidget = objectPath.get(isUiChanged, [
    widgetName,
    uiInputName,
  ])

  useEffect(() => {
    if (widgetName && renderCheckboxWidget) {
      let data = objectPath.get(isUiChanged, [widgetName, '@data'])
      if (data) {
        let value = objectPath.get(data, uiName)
        updateValue(uischema, value)
      }
      objectPath.del(isUiChanged, [widgetName, uiName])
      setIsUiChanged(isUiChanged)
    }
  }, [renderCheckboxWidget])

  useEffect(() => {
    if (widgetName && renderInputWidget) {
      let data = objectPath.get(isUiChanged, [widgetName, '@data'])
      if (data) {
        let value = objectPath.get(data, uiInputName)
        updateValue(input, value)
      }
      objectPath.del(isUiChanged, [widgetName, uiInputName])
      setIsUiChanged(isUiChanged)
    }
  }, [renderInputWidget])

  const updateValue = ({ key, name }, value, isOnChange) => {
    updateCheckboxInput({
      ...dataProps,
      value,
      isOnChange,
      uiKey: key,
      uiName: name,
    })
  }

  const updateInput = (value, isOnChange) => {
    updateValue({ key: uiInputKey, name: uiInputName }, value, isOnChange)
  }

  const updateCheckbox = (checked, isOnChange) => {
    let value = checked ? valueChecked : valueUnchecked
    updateValue({ key: uiKey, name: uiName }, value, isOnChange)
    if (!checked) {
      updateInput('')
    }
  }

  const copyInput = (value) => {
    let newFormData = copyDataByCondition(
      value,
      elementAction,
      uiInputKey,
      formData
    )
    setFormData(newFormData)
  }

  const copyCheckbox = (checked) => {
    let value = checked ? valueChecked : valueUnchecked
    let newFormData = copyDataByCondition(value, elementAction, uiKey, formData)
    setFormData(newFormData)
  }

  const getDefaultChecked = () => {
    return (
      defaultChecked === true || getCheckValue(defaultChecked) === valueChecked
    )
  }

  let mapCheckboxResponse = objectPath.get(data, ['@mapResponse', uiKey])
  useEffect(() => {
    if (mapCheckboxResponse) {
      onUpdateCondition(props, designContext, uiKey, data)
      objectPath.del(formData, ['@mapResponse', uiKey])
      setFormData(formData)
    }
  }, [mapCheckboxResponse])

  let mapInputResponse = objectPath.get(data, ['@mapResponse', uiInputKey])
  useEffect(() => {
    if (mapInputResponse) {
      onUpdateCondition(props, designContext, uiInputKey, data)
      objectPath.del(formData, ['@mapResponse', uiInputKey])
      setFormData(formData)
    }
  }, [mapInputResponse])

  useEffect(() => {
    if (value === undefined) {
      let storage = getStorage(jsonForm, uiInputName)
      let value
      if (isEmpty(storage)) {
        value = getDefaultChecked() ? valueChecked : valueUnchecked
      } else {
        value = storage
      }
      updateValue({ key: uiKey, name: uiName }, value)
    }
  }, [defaultChecked])

  useEffect(() => {
    if (inputValue === undefined) {
      let storage = getStorage(jsonForm, uiInputName)
      let value
      if (isEmpty(storage)) {
        value = defaultInput
      } else {
        value = storage
      }
      updateValue({ key: uiInputKey, name: uiInputName }, value)
    }
  }, [defaultInput])

  useEffect(() => {
    if (isResetData) {
      updateCheckbox(getDefaultChecked())
      updateValue({ key: uiInputKey, name: uiInputName }, inputValue)
    }
  }, [isResetData])

  useEffect(() => {
    if (isUpdate) {
      let uiKeyList = [uiKey, uiInputKey]
      let conditionKeys = objectPath.get(elementAction, 'conditionKey') || []
      conditionKeys.forEach((keyList = []) => {
        if (keyList.includes(uiKey) || keyList.includes(uiInputKey)) {
          keyList.forEach((key) => {
            if (!uiKeyList.includes(key)) {
              uiKeyList.push(key)
            }
          })
        }
      })
      debounceCallback(
        onCheckCondition,
        [designContext, data, uiKeyList],
        'on-check-condition'
      )
      if (arrayKey) {
        let syncData = objectPath.get(isUiChanged, 'syncData') || {}
        Object.keys(syncData).forEach((arrayKey) => {
          setIsUiChanged(isUiChanged)
          debounceCallback(
            onUpdateCondition,
            [
              props,
              designContext,
              arrayKey,
              data,
              {
                isOnChange: isUpdate,
                isArray,
                arrayKey,
              },
            ],
            `onInputUpdateArrayCondition-${arrayKey}`
          )
        })
      }
      if (isDynamic) {
        debounceCallback(
          onUpdateCondition,
          [
            props,
            designContext,
            dynamicKey,
            '',
            { isOnChange: isUpdate, isDynamic, dynamicKey, dynamicIndex },
          ],
          `onInputUpdateArrayCondition-${dynamicKey}-${dynamicIndex}`
        )
      }
      setIsUpdate(false)
      debounceCallback(setIsInputSearch, [uiKey], 'onInputUpdate')
    }
  }, [isUpdate])

  return (
    <CustomCheckboxInput
      fieldName={fieldName}
      fieldKey={fieldKey}
      fieldInputName={fieldInputName}
      fieldInputKey={fieldInputKey}
      label={label}
      uischema={uischema}
      updateCheckbox={updateCheckbox}
      updateInput={updateInput}
      copyInput={copyInput}
      copyCheckbox={copyCheckbox}
      errors={errors}
      value={value}
      inputValue={inputValue}
      uiKey={uiKey}
      uiName={uiName}
      uiInputKey={uiInputKey}
      uiInputName={uiInputName}
      data={data}
      setIsOnChange={setIsOnChange}
    />
  )
}

const CheckboxInputControl = (props) => {
  return (
    <PropWrapper {...props}>
      {({ dataProps }) => <Children dataProps={dataProps} />}
    </PropWrapper>
  )
}

export default CheckboxInputControl
